import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { useCallback, useEffect, useState } from 'react';
import { getUser } from '../../../../redux/usersRedux';
import {
  getApiErrorMessage,
  getMixDeliveries,
} from '../../../../api/mix-deliveriesApi';

import {
  MIX_DELIVERY_STATUS,
  MIX_DELIVERY_STATUS_LABELS,
  MIX_STOCK_STATUS,
  MIX_STOCK_STATUS_LABELS,
} from '../types/mix-deliveries.types';

import MixDeliveryDetailsDialog, {
  DeliveryStatusChip,
  StockStatusChip,
} from './MixDeliveryDetailsDialog';

export default function MixDeliveriesTable({ refreshKey = 0, onDataChanged }) {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const user = useSelector((state) => getUser(state));
  const [status, setStatus] = useState('');
  const [stockStatus, setStockStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getMixDeliveries({
        page: pagination.page,
        limit: pagination.limit,
        status: status || undefined,
        stockStatus: stockStatus || undefined,
        search: search || undefined,
      });

      setRows(result.data ?? []);
      setPagination((current) => ({
        ...current,
        total: result.pagination?.total ?? 0,
        totalPages: result.pagination?.totalPages ?? 0,
      }));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, status, stockStatus, search]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries, refreshKey]);

  const handleApplySearch = () => {
    setPagination((current) => ({
      ...current,
      page: 1,
    }));

    setSearch(searchInput.trim());
  };

  const handleClearFilters = () => {
    setStatus('');
    setStockStatus('');
    setSearch('');
    setSearchInput('');

    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const handleStatusChanged = () => {
    loadDeliveries();
    onDataChanged?.();
  };

  return (
    <Stack spacing={2}>
      <Filters
        status={status}
        stockStatus={stockStatus}
        searchInput={searchInput}
        disabled={loading}
        onStatusChange={(value) => {
          setStatus(value);

          setPagination((current) => ({
            ...current,
            page: 1,
          }));
        }}
        onStockStatusChange={(value) => {
          setStockStatus(value);

          setPagination((current) => ({
            ...current,
            page: 1,
          }));
        }}
        onSearchInputChange={setSearchInput}
        onSearch={handleApplySearch}
        onClear={handleClearFilters}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Dostawa</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Odbiorca</TableCell>
              <TableCell align="right">Pozycje miksowe</TableCell>
              <TableCell align="right">Łączna ilość</TableCell>
              <TableCell>Kanapkowanie</TableCell>
              <TableCell>Status zapasu</TableCell>
              <TableCell>Status pracy</TableCell>
              <TableCell align="right">Akcja</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              rows.map((delivery) => (
                <TableRow key={delivery.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>
                      {delivery.deliveryNumber}
                    </Typography>

                    {delivery.deliveryType && (
                      <Typography variant="caption" color="text.secondary">
                        {delivery.deliveryType}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {formatDate(delivery.deliveryDate ?? delivery.plannedDate)}
                  </TableCell>

                  <TableCell>
                    {delivery.recipientName ?? delivery.recipientNumber ?? '—'}
                  </TableCell>

                  <TableCell align="right">
                    <Chip size="small" label={delivery.mixItemCount} />
                  </TableCell>

                  <TableCell align="right">
                    {formatQuantity(delivery.totalMixQuantity)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={delivery.sandwichingRequired ? 'TAK' : 'NIE'}
                      color={
                        delivery.sandwichingRequired ? 'warning' : 'default'
                      }
                      variant={
                        delivery.sandwichingRequired ? 'filled' : 'outlined'
                      }
                      sx={{
                        fontWeight: delivery.sandwichingRequired ? 700 : 400,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <StockStatusChip status={delivery.stockStatus} />
                  </TableCell>

                  <TableCell>
                    <DeliveryStatusChip status={delivery.status} />
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedDeliveryId(delivery.id)}
                    >
                      Szczegóły
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  Brak dostaw spełniających kryteria.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Wierszy na stronie:"
          onPageChange={(_event, page) => {
            setPagination((current) => ({
              ...current,
              page: page + 1,
            }));
          }}
          onRowsPerPageChange={(event) => {
            setPagination((current) => ({
              ...current,
              page: 1,
              limit: Number(event.target.value),
            }));
          }}
        />
      </TableContainer>

      <MixDeliveryDetailsDialog
        open={Boolean(selectedDeliveryId)}
        deliveryId={selectedDeliveryId}
        onClose={() => setSelectedDeliveryId(null)}
        onStatusChanged={handleStatusChanged}
        userRole={user?.users.role}
      />
    </Stack>
  );
}

function Filters({
  status,
  stockStatus,
  searchInput,
  disabled,
  onStatusChange,
  onStockStatusChange,
  onSearchInputChange,
  onSearch,
  onClear,
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{
          xs: 'column',
          lg: 'row',
        }}
        spacing={2}
        alignItems={{
          xs: 'stretch',
          lg: 'center',
        }}
      >
        <TextField
          size="small"
          label="Szukaj"
          placeholder="Dostawa, materiał lub nazwa"
          value={searchInput}
          disabled={disabled}
          onChange={(event) => onSearchInputChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onSearch();
            }
          }}
          sx={{
            minWidth: {
              xs: '100%',
              lg: 300,
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Status pracy</InputLabel>

          <Select
            value={status}
            label="Status pracy"
            disabled={disabled}
            onChange={(event) => onStatusChange(event.target.value)}
          >
            <MenuItem value="">Wszystkie</MenuItem>

            {Object.values(MIX_DELIVERY_STATUS).map((value) => (
              <MenuItem key={value} value={value}>
                {MIX_DELIVERY_STATUS_LABELS[value]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Status zapasu</InputLabel>

          <Select
            value={stockStatus}
            label="Status zapasu"
            disabled={disabled}
            onChange={(event) => onStockStatusChange(event.target.value)}
          >
            <MenuItem value="">Wszystkie</MenuItem>

            {Object.values(MIX_STOCK_STATUS).map((value) => (
              <MenuItem key={value} value={value}>
                {MIX_STOCK_STATUS_LABELS[value]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            ml: {
              lg: 'auto',
            },
          }}
        >
          <Button variant="contained" disabled={disabled} onClick={onSearch}>
            Szukaj
          </Button>

          <Button variant="outlined" disabled={disabled} onClick={onClear}>
            Wyczyść
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

function formatQuantity(value) {
  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 3,
  }).format(Number(value ?? 0));
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL').format(new Date(value));
}
